import { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function NoShowModal({ open, onClose, ride, onReported }) {
  const [reason, setReason] = useState("");
  const [photo, setPhoto] = useState(null);   // data URL
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Photo trop lourde (max 4 Mo)"); return; }
    const r = new FileReader();
    r.onload = () => setPhoto(r.result);
    r.readAsDataURL(f);
  };

  const getLoc = () => new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve({}),
      { maximumAge: 60000, timeout: 5000 }
    );
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { toast.error("Expliquez brièvement ce qui s'est passé"); return; }
    if (!photo) { toast.error("Une photo est obligatoire"); return; }
    setBusy(true);
    try {
      const loc = await getLoc();
      await api.post(`/rides/${ride.id}/no-show`, {
        reason: reason.trim(),
        photo_base64: photo,
        ...loc,
      });
      toast.success("No-show signalé");
      setReason(""); setPhoto(null);
      if (fileRef.current) fileRef.current.value = "";
      onReported?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="no-show-modal" className="bg-[#121212] border-zinc-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-amber-400 flex items-center gap-2">
            Signaler un no-show
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {ride && (
            <div className="bg-[#0e0e0e] border border-zinc-800 rounded p-3 text-xs text-zinc-400">
              <p className="text-white">{ride.route_label}</p>
              <p>{ride.pickup_date} à {ride.pickup_time}</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">Photo du lieu (obligatoire)</Label>
            <input
              ref={fileRef}
              data-testid="no-show-photo"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={pickPhoto}
              className="text-xs text-zinc-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-amber-500 file:text-black hover:file:bg-amber-600"
            />
            {photo && (
              <div className="relative mt-2 rounded overflow-hidden border border-zinc-800">
                <img src={photo} alt="No-show" className="w-full max-h-52 object-cover" />
                <button type="button" onClick={() => { setPhoto(null); if (fileRef.current) fileRef.current.value = ""; }} className="absolute top-1.5 right-1.5 bg-black/70 rounded-full p-1 text-white hover:bg-red-500">
                  <X size={14} />
                </button>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">Explication *</Label>
            <Textarea
              data-testid="no-show-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Attente 20 min devant l'adresse, client injoignable..."
              className="bg-[#1A1A1A] border-zinc-700 text-white"
            />
          </div>
          <p className="text-[11px] text-zinc-500">
            <Camera size={11} className="inline mr-1" /> La position GPS (si autorisée) et l'horodatage seront joints automatiquement.
          </p>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onClose?.()} className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-10">Annuler</Button>
            <Button data-testid="no-show-submit" type="submit" disabled={busy} className="flex-1 bg-amber-500 text-black font-semibold hover:bg-amber-600 h-10">
              {busy ? "Envoi..." : "Signaler le no-show"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
